import { accountStatuses, armedStatus, guardPatrolState, vsgModeStatus } from '$src/api/constants';
import { controlCenterApi } from '$src/api/control-center-api';
import { Notify } from '$src/utils/notify';
import {
  redirectToDeviceAuthorization,
  redirectToEnableProfessionalMonitoring,
  redirectToOnboardingIntroduction,
  redirectToProfessionalMonitoringSettings,
} from '$src/utils/redirect';
import { action, computed, makeObservable, observable } from 'mobx';

export default class ModesStore {
  vsgErrorState = '';
  monitoringStatus = '';
  armedStatus = '';
  pauseState = false;
  guardPatrolFeatureState = null;

  fetchVSGModeStatus = async (userId, locationId) => {
    const response = await controlCenterApi.fetchVSGModeStatus(userId, locationId);
    this.setArmedStatus(response?.monitoring);
    [this.vsgErrorState] = response?.details?.reasons ? response.details.reasons : '';
  };

  fetchVSGState = async (userId, locationId) => {
    try {
      const { paused } = await controlCenterApi.fetchVSGState(userId, locationId);
      this.setPauseState(paused);
    } catch (e) {
      Notify.error('Failed fetching VSG State.', e);
    }
  };

  handleEndAlarmPractice = async (userId, locationId) => {
    await this.endAlarmPractice(userId, locationId);
    await this.fetchVSGModeStatus(userId, locationId);
    Notify.success(`Successfully ended alarm practice.`);
  };

  get vsgMode() {
    const locationId = this.rootStore.locationContextStore.location?.locationId;
    switch (this.vsgErrorState) {
      case vsgModeStatus.NO_RING_PROTECT:
        return {
          title: 'Virtual Security Guard is currently inactive',
          description: `Turn on Alarm Professional Monitoring and re-arm your system to activate Virtual Security Guard.`,
          action: 'Turn on Monitoring',
          'data-testid': 'turn-on-professional-monitoring',
          onClick: () => redirectToEnableProfessionalMonitoring(locationId),
        };
      case accountStatuses.PRACTICE:
        return {
          title: 'Virtual Security Guard is currently inactive',
          description: `End Alarm Practice, switch to Professional Monitoring, and re-arm your alarm to activate Virtual Security Guard.`,
          action: 'End Alarm Practice',
          'data-testid': 'exit-practice-mode',
          onClick: () =>
            this.handleEndAlarmPractice(this.rootStore.locationContextStore.locationOwnerId, locationId),
          shouldRenderLoading: true,
        };
      case vsgModeStatus.NO_DEVICES:
        return {
          title: 'Virtual Security Guard is currently inactive',
          description: 'Enroll at least one eligible device to activate Virtual Security Guard.',
          action: 'Enroll Device',
          'data-testid': 'enroll-device',
          onClick: () => redirectToDeviceAuthorization(locationId, { sider: false }),
        };

      case vsgModeStatus.INCOMPLETE:
        return {
          title: 'Virtual Security Guard is currently inactive',
          description: 'Complete setup to activate Virtual Security Guard.',
          action: 'Complete Setup',
          'data-testid': 'complete-setup',
          onClick: () => redirectToOnboardingIntroduction(locationId),
        };
      case vsgModeStatus.AUTH_NOT_FOUND:
        return {
          title: 'Virtual Security Guard is currently inactive',
          description: 'Complete authorization and re-arm your system to activate Virtual Security Guard.',
          action: 'Authorize',
          'data-testid': 'authorize',
        };
      case vsgModeStatus.PENDING_MODE:
        return {
          title: 'Virtual Security Guard is currently inactive',
          description: 'Active Alarm Professional Monitoring or SOS is required to activate Virtual Security Guard.',
          action: 'View Monitoring Status',
          'data-testid': 'pending-mode',
          onClick: () =>
            redirectToProfessionalMonitoringSettings(locationId),
        };
      case vsgModeStatus.SOS_OFF:
        return {
          title: 'Virtual Security Guard is currently inactive',
          description: 'Turn on SOS and re-arm your location to activate Virtual Security Guard.',
          action: 'View Monitoring Status',
          'data-testid': 'sos-off',
          onClick: () =>
            redirectToProfessionalMonitoringSettings(locationId),
        };
      case vsgModeStatus.MISSING_MONITORING_ZONE:
        // No ownership gate: the Set Up action takes no privileged action in Control Center.
        // Whatever the consumer wires to it either hands off to the Ring app/web Watch Zones
        // surface, which authorizes the user itself, or shows an informational modal.
        if (this.rootStore.sessionStore.featureFlags?.['stark-feature-next-video-monitoring-zones']?.enabled) {
          return {
            title: 'Set up the Watch Zones to show guards where to focus while monitoring an event.',
            action: 'Set Up',
            'data-testid': 'set-up-monitoring-zone',
          }
        }
        return null;
      case vsgModeStatus.SOS_SETUP_INCOMPLETE:
        return {
          title: 'Virtual Security Guard is currently inactive',
          description: 'Setup SOS and re-arm your location to activate Virtual Security Guard.',
          action: 'View Monitoring Status',
          'data-testid': 'sos-setup-incomplete',
          onClick: () =>
            redirectToProfessionalMonitoringSettings(locationId),
        };
      default:
        return null;
    }
  }

  setMonitoringStatus = (status) => {
    this.monitoringStatus = status;
  };

  setArmedStatus = (status) => {
    this.armedStatus = status;
  };

  fetchMonitoringStatus = async (userId, locationId) => {
    try {
      const { status, featureState } = await controlCenterApi.fetchMonitoringStatus(userId, locationId);
      this.monitoringStatus = status;
      this.guardPatrolFeatureState = featureState?.find((f) => f.name === 'guardPatrol') ?? null;
    } catch (e) {
      Notify.error('ModesStore: fetchMonitoringStatus failure', e);
    }
  };

  endAlarmPractice = async (userId, locationId) => {
    try {
      await controlCenterApi.endAlarmPractice(userId, locationId);
    } catch (e) {
      Notify.error('Failed ending alarm practice.');
    }
  };

  changeVSGState = async (userId, locationId, paused) => {
    try {
      await controlCenterApi.changeVSGState(userId, locationId, paused);

      this.setPauseState(paused);
    } catch (e) {
      Notify.error('Failed changing VSG State.');
    }
  };

  setPauseState = (pauseState) => {
    this.pauseState = pauseState;
  };

  get isMonitoringUnsupportedStatus() {
    return this.monitoringStatus === accountStatuses.UNSUPPORTED;
  }

  get isMonitoringStatusSOSOn() {
    return this.monitoringStatus === accountStatuses.SOS_ON;
  }

  get isMonitoringStatusSOSOff() {
    return this.monitoringStatus === accountStatuses.SOS_OFF;
  }

  get isMonitoringStatusSOSSetupIncomplete() {
    return this.monitoringStatus === accountStatuses.SOS_SETUP_INCOMPLETE;
  }

  get isPracticeMode() {
    return this.monitoringStatus === accountStatuses.PRACTICE;
  }

  get isArmed() {
    return this.armedStatus !== armedStatus.INACTIVE;
  }

  get isPendingMode() {
    return this.monitoringStatus === accountStatuses.PENDING;
  }

  get isPaused() {
    return this.pauseState;
  }

  get isGuardPatrolActive() {
    return this.guardPatrolFeatureState?.state === guardPatrolState.ACTIVE;
  }

  constructor(rootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      vsgErrorState: observable,
      armedStatus: observable,
      pauseState: observable,
      monitoringStatus: observable,
      guardPatrolFeatureState: observable,
      fetchVSGModeStatus: action,
      setArmedStatus: action,
      fetchMonitoringStatus: action,
      endAlarmPractice: action,
      fetchVSGState: action,
      changeVSGState: action,
      setPauseState: action,
      vsgMode: computed,
      isMonitoringUnsupportedStatus: computed,
      isMonitoringStatusSOSOn: computed,
      isMonitoringStatusSOSOff: computed,
      isMonitoringStatusSOSSetupIncomplete: computed,
      isPracticeMode: computed,
      isArmed: computed,
      isPendingMode: computed,
      isPaused: computed,
      isGuardPatrolActive: computed,
    });
  }
}
